/**
 * Shop supplies: fasteners, finishes — things recipes use up. Unlike
 * materials, consumables aren't physical objects you carry around the shop;
 * they live in one shop-wide stock (GameState.consumables) and operations
 * draw from it directly. Bought by the pack in the store's supplies aisle;
 * some come back as salvage (nails from dismantled pallets).
 *
 * How they flow: an operation's requiredConsumables are checked and
 * deducted when the work starts — no refunds on abandonment — and
 * consumableOutputs are added when it completes, which is how salvage
 * pays out (a pried pallet nail comes back as a nail). Fastened builds
 * are the exception: they carry no requiredConsumables at all, because
 * each fastener is spent the moment it's driven and comes back if it's
 * pulled (sim/commands/assembly-commands.ts); their bill is
 * blueprintFastenerCost, one per crossing, read through
 * operationSupplies wherever a recipe's supplies are shown.
 *
 * Clamps are deliberately NOT a consumable: a glue-up borrows them and
 * the cure gives them back — a returnable pool, not a stock that
 * depletes. See Clamp.ts.
 */
export interface ConsumableType {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  /** Display unit for amounts, e.g. "nails" or "oz". */
  readonly unit: string;
  /** The store SKU: how much one purchase adds, and what it costs. */
  readonly packName: string;
  readonly packSize: number;
  readonly packPrice: number;
}

export const CONSUMABLE_TYPES = {
  nails: {
    id: "nails",
    name: "Nails",
    description:
      "Common nails. Consumed by nailed assembly; dismantling a pallet returns some.",
    unit: "nails",
    packName: "Box of Nails",
    packSize: 50,
    packPrice: 6,
  },
  screws: {
    id: "screws",
    name: "Screws",
    description:
      "Wood screws. Consumed by screwed assembly; not recovered from salvage.",
    unit: "screws",
    packName: "Box of Screws",
    packSize: 50,
    packPrice: 8,
  },
  mineralOil: {
    id: "mineralOil",
    name: "Mineral Oil",
    description:
      "Food-safe finishing oil. Consumed when oiling a cutting board.",
    unit: "oz",
    packName: "Mineral Oil Bottle",
    packSize: 16,
    packPrice: 10,
  },
} satisfies { [id: string]: ConsumableType };

export type ConsumableId = keyof typeof CONSUMABLE_TYPES;

/** A quantity of one consumable — a recipe cost, or a salvage yield. */
export interface ConsumableAmount {
  readonly id: ConsumableId;
  readonly amount: number;
}

/** The shop-wide stock of every consumable. */
export type ConsumableStock = Readonly<Record<ConsumableId, number>>;

export const NO_CONSUMABLES: ConsumableStock = {
  nails: 0,
  screws: 0,
  mineralOil: 0,
};

export function hasConsumables(
  stock: ConsumableStock,
  costs: ReadonlyArray<ConsumableAmount>,
): boolean {
  return costs.every((cost) => (stock[cost.id] ?? 0) >= cost.amount);
}

export function subtractConsumables(
  stock: ConsumableStock,
  costs: ReadonlyArray<ConsumableAmount>,
): ConsumableStock {
  return costs.reduce(
    (next, cost) => ({
      ...next,
      [cost.id]: (next[cost.id] ?? 0) - cost.amount,
    }),
    stock,
  );
}

export function addConsumables(
  stock: ConsumableStock,
  additions: ReadonlyArray<ConsumableAmount>,
): ConsumableStock {
  return additions.reduce(
    (next, addition) => ({
      ...next,
      [addition.id]: (next[addition.id] ?? 0) + addition.amount,
    }),
    stock,
  );
}

/** "8 nails", but "4 oz Mineral Oil" when the unit isn't the name. */
export function consumableLabel(cost: ConsumableAmount): string {
  const type = CONSUMABLE_TYPES[cost.id];
  return type.unit === type.name.toLowerCase()
    ? `${cost.amount} ${type.unit}`
    : `${cost.amount} ${type.unit} ${type.name}`;
}
